<template>
  <transition name="filter-job-categories-modal" appear>
    <div
      v-if="featureEnabled && value"
      class="filter-job-categories-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="filter-job-categories-modal-title"
      @mousedown.self="handleClose"
    >
      <div class="filter-job-categories-modal__card">
        <div class="filter-job-categories-modal__header">
          <span id="filter-job-categories-modal-title" class="filter-job-categories-modal__title">
            Filter job categories
          </span>
          <button
            type="button"
            class="filter-job-categories-modal__close-btn"
            aria-label="Close"
            @click="handleClose"
          >
            <Icon name="popup_close" />
          </button>
        </div>

        <div class="filter-job-categories-modal__filters-row">
          <div class="filter-job-categories-modal__column">
            <span class="filter-job-categories-modal__column-title">Status</span>
            <div class="filter-job-categories-modal__options">
              <CheckboxInput
                v-for="option in statusOptions"
                :key="option.value"
                :label="option.label"
                :value="statuses[option.value]"
                @input="updateStatus(option.value, $event)"
              />
            </div>
          </div>

          <div
            class="filter-job-categories-modal__column filter-job-categories-modal__column--radio"
          >
            <RadioInput
              label="Include archived"
              :availableItems="archivedOptions"
              :checkedItem="includeArchived ? 'Yes' : 'No'"
              @changeradio="onArchivedChange"
            />
          </div>

          <div
            class="filter-job-categories-modal__column filter-job-categories-modal__column--radio"
          >
            <RadioInput
              label="Used"
              :availableItems="usedOptions"
              :checkedItem="usedCheckedLabel"
              @changeradio="onUsedChange"
            />
          </div>
        </div>

        <div class="filter-job-categories-modal__footer">
          <button type="button" class="filter-job-categories-modal__reset" @click="handleReset">
            Reset to defaults
          </button>
          <StandardButton
            type="magenta"
            variant="primary"
            size="small"
            label="Apply"
            @click="handleApply"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, getCurrentInstance } from "vue";
import Icon from "@components/icons/Icon.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import CheckboxInput from "@components/Checkbox/CheckboxInput.vue";
import RadioInput from "@components/Radio/RadioInput.vue";
import { LD_FLAG_IDEA_MDP_566 } from "@components/constants/launchDarklyFlagKeys";

/**
 * ER-11408 — FilterJobCategoriesModal (Organization > Job Categories filter modal).
 *
 * Follows the Filter Modal Standard used by the sibling filter modals
 * (FilterRolesModal / FilterPositionsModal / PayRangeTableFilterModal): a Status
 * checkbox group (Draft/Active/Retired), an "Include archived" radio (No/Yes, No
 * default) and a "Used" radio (No Filter/Used/Not Used, No Filter default), with
 * Reset to defaults / Apply footer actions. The parent owns data fetching and
 * applying the emitted payload; this component only re-emits intent through
 * `apply`, `reset`, `input` and `close`.
 *
 * Values are stable lowercase snake_case end-to-end:
 *   - statuses:        { draft, active, retired }  (checkbox booleans)
 *   - includeArchived: false (default) | true
 *   - used:            no_filter (default) | used | not_used
 *
 * Shipped behind the linked idea flag (idea-MDP-566, "Job Category — Equal-Value
 * Grouping for Fair Pay (EU Directive Alignment)"). Kill-switch semantics: renders
 * by default; set the flag to false to keep the modal closed until a screen wires
 * it.
 */
const props = defineProps({
  value: { type: Boolean, default: false },
  initialStatuses: {
    type: Object,
    default: () => ({ draft: true, active: true, retired: false }),
  },
  initialIncludeArchived: { type: Boolean, default: false },
  initialUsed: {
    type: String,
    default: "no_filter",
    validator: (v) => ["no_filter", "used", "not_used"].includes(v),
  },
});

const emit = defineEmits(["input", "close", "apply", "reset"]);

const DEFAULT_STATUSES = { draft: true, active: true, retired: false };
const DEFAULT_INCLUDE_ARCHIVED = false;
const DEFAULT_USED = "no_filter";

const statusOptions = [
  { label: "Draft", value: "draft" },
  { label: "Active", value: "active" },
  { label: "Retired", value: "retired" },
];

const archivedOptions = [{ label: "No" }, { label: "Yes" }];
const usedOptions = [{ label: "No Filter" }, { label: "Used" }, { label: "Not Used" }];

const USED_LABEL_BY_VALUE = {
  no_filter: "No Filter",
  used: "Used",
  not_used: "Not Used",
};

const USED_VALUE_BY_LABEL = {
  "No Filter": "no_filter",
  Used: "used",
  "Not Used": "not_used",
};

const statuses = ref({ ...DEFAULT_STATUSES, ...(props.initialStatuses || {}) });
const includeArchived = ref(props.initialIncludeArchived);
const used = ref(props.initialUsed);

const usedCheckedLabel = computed(() => USED_LABEL_BY_VALUE[used.value] || "No Filter");

const app = getCurrentInstance();
const { proxy } = app || {};
const featureEnabled = ref(true);

watch(
  () => props.value,
  (open) => {
    if (open) syncFromProps();
  },
  { immediate: true }
);

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
  checkFeatureFlag();
});

async function checkFeatureFlag() {
  try {
    featureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_566, true)) !== false;
  } catch {
    featureEnabled.value = true;
  }
}

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
});

function syncFromProps() {
  statuses.value = { ...DEFAULT_STATUSES, ...(props.initialStatuses || {}) };
  includeArchived.value = props.initialIncludeArchived;
  used.value = props.initialUsed;
}

function updateStatus(key, checked) {
  statuses.value = { ...statuses.value, [key]: !!checked };
}

function onArchivedChange(payload) {
  includeArchived.value = payload?.value === "Yes";
}

function onUsedChange(payload) {
  used.value = USED_VALUE_BY_LABEL[payload?.value] ?? DEFAULT_USED;
}

function handleReset() {
  statuses.value = { ...DEFAULT_STATUSES };
  includeArchived.value = DEFAULT_INCLUDE_ARCHIVED;
  used.value = DEFAULT_USED;
  emit("reset");
}

function handleApply() {
  emit("apply", {
    statuses: { ...statuses.value },
    includeArchived: includeArchived.value,
    used: used.value,
  });
  emit("input", false);
}

function handleClose() {
  emit("input", false);
  emit("close");
}

function handleKeydown(event) {
  if (event.key === "Escape" && props.value && featureEnabled.value) handleClose();
}
</script>

<style scoped>
.filter-job-categories-modal-enter-active,
.filter-job-categories-modal-leave-active {
  transition: opacity 180ms ease;
}

.filter-job-categories-modal-enter-from,
.filter-job-categories-modal-leave-to {
  opacity: 0;
}

.filter-job-categories-modal-enter-active .filter-job-categories-modal__card,
.filter-job-categories-modal-leave-active .filter-job-categories-modal__card {
  transition: transform 200ms cubic-bezier(0.22, 1, 0.36, 1), opacity 180ms ease;
}

.filter-job-categories-modal-enter-from .filter-job-categories-modal__card,
.filter-job-categories-modal-leave-to .filter-job-categories-modal__card {
  transform: translateY(8px) scale(0.98);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .filter-job-categories-modal-enter-active,
  .filter-job-categories-modal-leave-active,
  .filter-job-categories-modal-enter-active .filter-job-categories-modal__card,
  .filter-job-categories-modal-leave-active .filter-job-categories-modal__card {
    transition: none !important;
  }
}

.filter-job-categories-modal__overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(16, 24, 40, 0.5);
  font-family: Inter, sans-serif;
}

.filter-job-categories-modal__card {
  width: 100%;
  max-width: 1032px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 8px 8px -4px rgba(16, 24, 40, 0.03), 0 20px 24px -4px rgba(16, 24, 40, 0.08);
  box-sizing: border-box;
}

.filter-job-categories-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.filter-job-categories-modal__title {
  color: #101828;
  font-size: 18px;
  font-weight: 600;
  line-height: normal;
}

.filter-job-categories-modal__close-btn {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 4px;
  margin: -4px;
  cursor: pointer;
  color: #98a2b3;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.filter-job-categories-modal__close-btn:hover {
  background: rgba(152, 162, 179, 0.12);
}

.filter-job-categories-modal__close-btn:focus-visible,
.filter-job-categories-modal__reset:focus-visible {
  outline: 2px solid #98a2b3;
  outline-offset: 2px;
}

.filter-job-categories-modal__filters-row {
  display: flex;
  gap: 32px;
  align-items: flex-start;
  width: 100%;
}

.filter-job-categories-modal__column {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-job-categories-modal__column-title {
  padding-left: 8px;
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.filter-job-categories-modal__options {
  display: flex;
  flex-direction: column;
  padding-left: 8px;
}

.filter-job-categories-modal__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.filter-job-categories-modal__reset {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 4px;
  margin: -4px;
  color: #475467;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: normal;
  cursor: pointer;
  border-radius: 6px;
}

.filter-job-categories-modal__reset:hover {
  color: #344054;
  background: rgba(152, 162, 179, 0.08);
}
</style>
